/**
 * Which linked player (and which of their teams) a parent/guardian last looked at, remembered per
 * signed-in user in localStorage. Every access is wrapped: blocked site data just means the first
 * player is shown. Sign-out clears every user's entry (`AuthContext.logout`), so the next person on
 * this browser never inherits it. A stored player who is no longer linked falls back to the first one
 * (`resolveFamilyPlayerSelection`).
 */
import type { TravelBallFamilyPlayer, TravelBallFamilyTeam } from '../../../types/travelBallFamily';
import { playerSelectionKey } from './familyLinkCopy';

const KEY_PREFIX = 'rosterpilot.tbFamilyPlayer.';

export interface StoredFamilyPlayerSelection {
  /** `playerSelectionKey`: the player's user id, or `link:{id}` while they have not joined. */
  playerKey: string;
  teamId: string | null;
}

const isStored = (value: unknown): value is StoredFamilyPlayerSelection => {
  const v = value as Partial<StoredFamilyPlayerSelection> | null;
  return (
    !!v &&
    typeof v.playerKey === 'string' &&
    (v.teamId === null || typeof v.teamId === 'string')
  );
};

export const loadFamilyPlayerSelection = (
  userId: string | null | undefined,
): StoredFamilyPlayerSelection | null => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(KEY_PREFIX + userId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStored(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const saveFamilyPlayerSelection = (
  userId: string | null | undefined,
  selection: StoredFamilyPlayerSelection,
): void => {
  if (!userId) return;
  try {
    localStorage.setItem(KEY_PREFIX + userId, JSON.stringify(selection));
  } catch {
    // Storage unavailable: the choice lasts until the page reloads, which is safe.
  }
};

/** Sign-out: forget every user's choice on this browser. */
export const clearFamilyPlayerSelections = (): void => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key?.startsWith(KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Nothing to clear.
  }
};

export interface ResolvedFamilyPlayerSelection {
  player: TravelBallFamilyPlayer | null;
  team: TravelBallFamilyTeam | null;
}

/**
 * The player and team to show: the stored ones while they are still linked and on that team,
 * otherwise the first player and their first team.
 */
export const resolveFamilyPlayerSelection = (
  players: TravelBallFamilyPlayer[],
  stored: StoredFamilyPlayerSelection | null,
): ResolvedFamilyPlayerSelection => {
  const player = players.find((p) => playerSelectionKey(p) === stored?.playerKey) ?? players[0] ?? null;
  if (!player) return { player: null, team: null };
  const team =
    (stored?.playerKey === playerSelectionKey(player)
      ? player.teams.find((t) => t.teamId === stored.teamId)
      : undefined) ??
    player.teams[0] ??
    null;
  return { player, team };
};
